import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    Link,
    Modal,
    TextField,
    Tooltip,
    Typography,
    useTheme,
    capitalize,
    Chip,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import queryGraphql from "../../helpers/ApiInterface";
import { useAuth } from "../../helpers/AuthProvider";
import { Close } from "@mui/icons-material";
import { Formik, ErrorMessage, Form } from "formik";
import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
import MiniBot from "../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer";

// --- Interfaces ---
interface CostLabelPair {
    cost: string;
    label: string;
}

interface APIData {
    id: string;
    name: string;
    cloudProvider: string;
    environment: string;
    status: string;
    reasonForRejection?: string;
    costLabelPairs: CostLabelPair[];
    apiBackendOwner: { id: string; name: string; googleProfilePicture?: string };
    apiBackendApprover?: { id: string; name: string; googleProfilePicture?: string };
    application: { id: string; applicationName: string; };
    service: { id: string; serviceName: string; };
    codeTemplate: string;
    path: string;
    httpMethod: string;
    apiVersion: string;
    description: string;
    mockRequest: string;
    mockResponse: string;
    serviceType: string;
}

const ValidationSchema = Yup.object().shape({
    reasonForRejection: Yup.string().required("Reason for rejection is required").min(5, "Reason must be at least 5 characters long"),
});

// --- Helper Component ---
const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null; isLink?: boolean }> = ({ label, value, isLink = false }) => {
    const displayValue = value ?? '-';
    
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            {isLink && typeof displayValue === 'string' ? (
                 <Link href={displayValue} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '14px', fontFamily: 'Gilroy-Semibold', wordBreak: 'break-all' }}>
                    {/* FIX: Use displayValue here, which is guaranteed to be a string */}
                    {displayValue.substring(displayValue.lastIndexOf('/') + 1)}
                </Link>
            ) : (
                <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>
                    {typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}
                </Typography>
            )}
        </Box>
    );
};


const APIApproveFormPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [api, setApi] = useState<APIData | null>(null);
    const [openModal, setOpenModal] = useState(false);
    const [openApproveModal, setOpenApproveModal] = useState(false);
    const [apiEndpoint, setApiEndpoint] = useState<string | null>(null);
    const [apiRepo, setApiRepo] =  useState<string | null>(null);
    // -- NEW STATE for Jira Key
    const [jiraIssueKey, setJiraIssueKey] = useState<string | null>(null);


    useEffect(() => {
        const fetchData = async () => {
            if (!id) return;
            try {
                // -- UPDATED QUERY to fetch both API details and resource status (for Jira key)
                const data = await queryGraphql(`
                    query GetApiDetailsForApproval($id: String!) {
                        getAPIBackend(id: $id) {
                            id
                            name
                            cloudProvider
                            environment
                            status
                            apiBackendOwner { id, name, googleProfilePicture }
                            apiBackendApprover { id, name, googleProfilePicture }
                            application { id, applicationName }
                            service { id, serviceName }
                            reasonForRejection
                            costLabelPairs { cost, label }
                            codeTemplate
                            path
                            httpMethod
                            apiVersion
                            description
                            mockRequest
                            mockResponse
                            serviceType
                        }
                        resourceStatus(id: $id) {
                            jiraIssueKey
                            projectUrl
                            solutionEndpointUrl
                        }
                    }`, { id: id });

                // -- UPDATED STATE SETTING
                if (data.getAPIBackend) {
                    setApi(data.getAPIBackend);
                }
                if (data.resourceStatus) {
                    setJiraIssueKey(data.resourceStatus.jiraIssueKey);
                    setApiRepo(data.resourceStatus.projectUrl);
                    setApiEndpoint(data.resourceStatus.solutionEndpointUrl);
                }
            } catch (error) { 
                console.error("Error fetching API details:", error); 
            }
        };
        fetchData();
    }, [id]);
    

    const handleApprove = async () => {
        if (!id) return;
        try {
            // This mutation now triggers the backend logic to update Jira
            const approvalData = await queryGraphql(
                `mutation ApproveAPIBackend($input: ApproveAPIBackendInput!) { approveAPIBackend(input: $input) { id } }`,
                { input: { id } }
            );
            if (approvalData.approveAPIBackend?.id) {
                setOpenApproveModal(false);
                navigate("/apis/requests");
            }
        } catch (error) { console.error("Error during approval:", error); }
    };
    
    const handleReject = async (reasonForRejection: string) => {
        if (!id) return;
        try {
            // This mutation now triggers the backend logic to update Jira
            const rejectionData = await queryGraphql(
                `mutation RejectAPIBackend($input: RejectAPIBackendInput!) { rejectAPIBackend(input: $input) { id } }`,
                { input: { id: id, reasonForRejection } }
            );
            if (rejectionData.rejectAPIBackend?.id) {
                setOpenModal(false);
                navigate("/apis/requests");
            }
        } catch (error) { console.error("Error during rejection:", error); }
    };

    const statusDisplay = api?.status ? capitalize(api.status) : "Status Unknown";
    const statusBgColor = api?.status === "active" || api?.status === "approved" ? "#12B76A10" : api?.status === "rejected" ? "#F0443810" : "#FDB02210";
    const statusIcon = api?.status === "active" || api?.status === "approved" ? "/assets/tickcircle.svg" : api?.status === "rejected" ? "/assets/reject.svg" : "/assets/clock.svg";
    
    // -- NEW VARIABLE for Jira Link
    // const jiraLink = jiraIssueKey ? `${process.env.REACT_APP_JIRA_SITE}/browse/${jiraIssueKey}` : undefined;
    const jiraLink = jiraIssueKey ? `https://searce-intellicore.atlassian.net/jira/software/projects/BTS/boards/1` : undefined;



    if (!api) return <Container sx={{p: 4, textAlign: 'center'}}>Loading details...</Container>;

    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;

    return (
        <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/apis/requests" onClick={(e) => { e.preventDefault(); navigate("/apis/requests") }}> API Backend Requests </Link>
                    <Typography color="text.primary">API Backend Details</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={1}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/apis/requests")}>
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">API Backend Details</Typography>
                </Box>
            </Box>
            <Grid container justifyContent="center">
                <Grid item xs={11}> 
                    <Box my={2}>
                        <Box bgcolor={statusBgColor} borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box>
                                    <Typography variant="h5" fontFamily="Gilroy-Bold" gutterBottom>{api.name}</Typography>
                                    <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{`Resource Owner: ${api.apiBackendOwner?.name}`}</Typography>
                                </Box>
                                <Box display="flex" alignItems="center">
                                    <img src={statusIcon} alt="Status Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" sx={{textTransform: 'capitalize'}}>{statusDisplay}</Typography>
                                </Box>
                            </Box>
                        </Box>
                        
                        <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
                            <Grid container spacing={2.5}>
                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Application Name" value={api.application?.applicationName} /></Grid>
                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Service Name" value={api.service?.serviceName} /></Grid>
                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Cloud Provider" value={api.cloudProvider?.toUpperCase()} /></Grid>
                                {/* -- NEW: Display Jira Ticket -- */}
                                {/* {jiraLink && <Grid item xs={12} sm={6} md={3}><DetailItem label="Jira Ticket" value={jiraLink} isLink={true} /></Grid>} */}
                                {jiraLink && jiraIssueKey && (
    <Grid item xs={12} sm={6} md={3}>
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">Jira Ticket</Typography>
            <Link 
                href={jiraLink} 
                target="_blank" 
                rel="noopener noreferrer" 
                sx={{ fontSize: '14px', fontFamily: 'Gilroy-Semibold', wordBreak: 'break-all' }}
            >
                {jiraIssueKey}
            </Link>
        </Box>
    </Grid>
)}
                            </Grid>

                            <Divider sx={{ my: 2.5 }} />
                            <Typography variant="subtitle1" fontFamily="Gilroy-SemiBold" sx={{ mb: 2 }}>API Backend Specification</Typography>
                            <Grid container spacing={2.5}>
                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Path" value={api.path} /></Grid>
                                <Grid item xs={12} sm={6} md={3}><DetailItem label="HTTP Method" value={api.httpMethod} /></Grid>
                                <Grid item xs={12} sm={6} md={3}><DetailItem label="API Version" value={api.apiVersion} /></Grid>
                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Code Template" value={api.codeTemplate} /></Grid>
                                {apiRepo && <Grid item xs={12} sm={6} md={3}>
                                     <Typography 
                                                variant="caption" 
                                                display="block" 
                                                sx={{ fontFamily: 'Gilroy-Regular', fontSize: '12px', color: '#666666' }}
                                            >
                                                API Backend Repo
                                            </Typography>
                                            <Tooltip
                                                title={
                                                    <>
                                                        <Typography 
                                                            fontFamily="Gilroy-Bold" 
                                                            fontSize="14px"
                                                        >
                                                            API Backend Repo
                                                        </Typography>
                                                        
                                                        {/* Conditionally render Link vs. plain text */}
                                                        {apiRepo && apiRepo !== "Loading..." ? (
                                                            <Link
                                                                href={`${apiRepo}/-/tree/feature1_dev/apis/${api.name}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                sx={{
                                                                    color: '#ffffff',
                                                                    textDecoration: 'underline',
                                                                    wordBreak: 'break-all' // Ensures long URLs wrap
                                                                }}
                                                            >
                                                                Repository URL
                                                            </Link>
                                                        ) : (
                                                            <Typography 
                                                                fontFamily="Gilroy-Regular" 
                                                                fontSize="12px" 
                                                                sx={{ color: '#ffffff' }}
                                                            >
                                                                Loading...
                                                            </Typography>
                                                        )}
                                                    </>
                                                }
                                                slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
                                            >
                                                <IconButton sx={{ p: 0 }}>
                                                    <Avatar
                                                        src="/assets/info.svg"
                                                        alt="info"
                                                        sx={{ height: 16, width: 16, cursor: 'pointer' }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                    </Grid>}
                            </Grid>
                        </Box>
                    </Box>
                    
                    {api?.status === "pending" && (
                        <Box display="flex" justifyContent="flex-end" gap={2} my={3}>
                            <Button sx={{ color: "black",borderColor: "black",fontWeight: "bold",textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px" }} variant="outlined" size="large" disabled={isViewer} onClick={() => setOpenModal(true)}>Reject Request</Button>
                            <Button sx={{ backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px" }} variant="contained" size="large" disabled={isViewer} onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
                        </Box>
                    )}

                    <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
                        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 500 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px" }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Request</Typography><IconButton onClick={() => setOpenApproveModal(false)}><Close fontSize="small" /></IconButton></Box>
                            <Divider />
                            <Box p={3}><Typography>Are you sure you want to approve this API Backend request?</Typography></Box>
                            <Divider />
                            <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                                <Button variant="outlined" onClick={() => setOpenApproveModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                                <Button onClick={handleApprove} sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } }} variant="contained">Approve</Button>
                            </Box>
                        </Box>
                    </Modal>

                    <Modal open={openModal} onClose={() => setOpenModal(false)}>
                        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 700 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px" }}>
                            <Formik
                                initialValues={{ reasonForRejection: "" }}
                                validationSchema={ValidationSchema}
                                onSubmit={({ reasonForRejection }) => handleReject(reasonForRejection)}
                            >
                                {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                                    <Form id="reject-api-form">
                                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject API Backend Request</Typography><IconButton onClick={() => setOpenModal(false)}><Close fontSize="small" /></IconButton></Box>
                                        <Divider />
                                        <Box sx={{ p: 3 }}>
                                            <CompulsoryFieldsLabel fieldName="Comments" />
                                            <TextField variant="filled" fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
                                            <Box sx={{ color: "red", mt: 1 }}><ErrorMessage name="reasonForRejection" /></Box>
                                        </Box>
                                        <Divider />
                                        <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                                            <Button variant="outlined" onClick={() => setOpenModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                                            <Button type="submit" form="reject-api-form" disabled={!isValid || !dirty} sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)' } }} variant="contained">Reject</Button>
                                        </Box>
                                    </Form>
                                )}
                            </Formik>
                        </Box>
                    </Modal>
                </Grid>
            </Grid>
        </Container>
    );
};

const APIApproveFormPage: React.FC = () => {
    const theme = useTheme();
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(() => typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('isFaradayDrawerOpen') || 'true') : true);
    const handleDrawerStateChange = (isOpen: boolean) => {
        setIsDrawerOpen(isOpen);
        if (typeof window !== 'undefined') localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpen));
    };
    const currentMarginLeft = isDrawerOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"requests-apis"} onStateChange={handleDrawerStateChange} />
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
                <APIApproveFormPageContent />
            </Box>
        </Box>
    );
};

export default APIApproveFormPage;





// import {
//     Avatar,
//     Box,
//     Breadcrumbs,
//     Button,
//     Container,
//     Divider,
//     Grid,
//     IconButton,
//     Link,
//     Modal,
//     TextField,
//     Tooltip,
//     Typography,
//     useTheme,
//     capitalize,
//     Chip,
// } from "@mui/material";
// import React, { useState, useEffect } from "react";
// import * as Yup from "yup";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import { useNavigate, useParams } from "react-router-dom";
// import queryGraphql from "../../helpers/ApiInterface";
// import { useAuth } from "../../helpers/AuthProvider";
// import { Close } from "@mui/icons-material";
// import { Formik, ErrorMessage, Form } from "formik";
// import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
// import MiniBot from "../MiniBot";
// import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer";

// // --- Interfaces ---
// interface CostLabelPair {
//     cost: string;
//     label: string;
// }

// interface APIData {
//     id: string;
//     name: string;
//     cloudProvider: string;
//     environment: string;
//     status: string;
//     reasonForRejection?: string;
//     costLabelPairs: CostLabelPair[];
//     apiBackendOwner: { id: string; name: string; googleProfilePicture?: string }; // Renamed
//     apiBackendApprover?: { id: string; name: string; googleProfilePicture?: string }; // Renamed
//     application: { id: string; applicationName: string; };
//     service: { id: string; serviceName: string; };
    
//     // Flat properties (no longer in 'details.api')
//     codeTemplate: string;
//     path: string;
//     httpMethod: string;
//     apiVersion: string;
//     description: string;
//     mockRequest: string;
//     mockResponse: string;
//     serviceType: string;
// }
// const ValidationSchema = Yup.object().shape({
//     reasonForRejection: Yup.string().required("Reason for rejection is required").min(5, "Reason must be at least 5 characters long"),
// });

// // --- Helper Component ---
// const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
//     const displayValue = value ?? '-';
//     return (
//         <Box mb={2}>
//             <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
//             <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>
//                 {typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}
//             </Typography>
//         </Box>
//     );
// };

// const APIApproveFormPageContent: React.FC = () => {
//     const navigate = useNavigate();
//     const { id } = useParams<{ id: string }>();
//     const { user } = useAuth();
//     const [api, setApi] = useState<APIData | null>(null);
//     const [openModal, setOpenModal] = useState(false);
//     const [openApproveModal, setOpenApproveModal] = useState(false);
//     const [apiEndpoint, setApiEndpoint] = useState<string | null>(null);
//     const [apiRepo, setApiRepo] =  useState<string | null>(null);
//     useEffect(() => {
//         const fetchData = async () => {
//             if (!id) return;
//             try {
//                 const data = await queryGraphql(`
//                     query GetAPIBackend($getApiBackendId: String!) {
//                       getAPIBackend(id: $getApiBackendId) {
//                         id
//                         name
//                         cloudProvider
//                         environment
//                         status
//                         apiBackendOwner { id, name, googleProfilePicture }
//                         apiBackendApprover { id, name, googleProfilePicture }
//                         application { id, applicationName }
//                         service { id, serviceName }
//                         reasonForRejection
//                         costLabelPairs { cost, label }
//                         codeTemplate
//                         path
//                         httpMethod
//                         apiVersion
//                         description
//                         mockRequest
//                         mockResponse
//                         serviceType
//                       }
//                     }`, { getApiBackendId: id }); // <-- Variable name changed
//                 if (data.getAPIBackend) setApi(data.getAPIBackend); // <-- Response key changed
//             } catch (error) { console.error("Error fetching API details:", error); }
//         };
//         fetchData();
//     }, [id]);
//     useEffect(() => {
//             const fetchEndpointUrl = async () => {
//                 // Only run if we have an active API with an ID
//                 if (!api || api.status !== 'active' || !api.id) return;

//                 try {
//                     const data = await queryGraphql(
//                         `query Query($resourceStatusId: String!) {
//                         resourceStatus(id: $resourceStatusId) {
//                             solutionEndpointUrl
//                             projectUrl
//                         }
//                         }`, 
//                         { resourceStatusId: api.id }
//                     );
//                     if (data.resourceStatus?.solutionEndpointUrl) {
//                         setApiEndpoint(data.resourceStatus.solutionEndpointUrl);
//                         setApiRepo(data.resourceStatus.projectUrl);
//                     }
//                 } catch (error) {
//                     console.log("Error fetching API endpoint URL:", error);
//                 }
//             };

//             fetchEndpointUrl();
//         }, [api]); 

//     const handleApprove = async () => {
//         if (!id) return;
//        try {
//             const approvalData = await queryGraphql(
//                 `mutation ApproveAPIBackend($input: ApproveAPIBackendInput!) { approveAPIBackend(input: $input) { id } }`,
//                 { input: { id } }
//             );
//             if (approvalData.approveAPIBackend?.id) {
//                 setOpenApproveModal(false);
//                 navigate("/apis/requests");
//             }
//         } catch (error) { console.error("Error during approval:", error); }
//     };

//     const statusDisplay = api?.status ? capitalize(api.status) : "Status Unknown";
//     const statusBgColor = api?.status === "active" || api?.status === "approved" ? "#12B76A10" : api?.status === "rejected" ? "#F0443810" : "#FDB02210";
//     const statusIcon = api?.status === "active" || api?.status === "approved" ? "/assets/tickcircle.svg" : api?.status === "rejected" ? "/assets/.svg" : "/assets/clock.svg";

//     if (!api) return <Container sx={{p: 4, textAlign: 'center'}}>Loading details...</Container>;

//     return (
//         <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
//             <MiniBot />
//             <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
//                     <Link underline="hover" color="inherit" href="/apis/requests" onClick={(e) => { e.preventDefault(); navigate("/apis/requests") }}> API Backend Requests </Link>
//                     <Typography color="text.primary">API Backend Details</Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" mt={1}>
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/apis/requests")}>
//                         <ArrowBackIosNewIcon fontSize="small" />
//                     </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="18px">API Backend Details</Typography>
//                 </Box>
//             </Box>
//             <Grid container justifyContent="center">
//                 <Grid item xs={11}> 
//                     <Box my={2}>
//                         <Box bgcolor={statusBgColor} borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
//                             <Box display="flex" justifyContent="space-between" alignItems="flex-start">
//                                 <Box>
//                                     <Typography variant="h5" fontFamily="Gilroy-Bold" gutterBottom>{api.name}</Typography>
//                                     <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{`Resource Owner: ${api.apiBackendOwner?.name}`}</Typography>
//                                 </Box>
//                                 <Box display="flex" alignItems="center">
//                                     <img src={statusIcon} alt="Status Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" sx={{textTransform: 'capitalize'}}>{statusDisplay}</Typography>
//                                 </Box>
//                             </Box>
//                         </Box>
                        
//                         <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
//                             <Grid container spacing={2.5}>
//                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="Application Name" value={api.application?.applicationName} /></Grid>
//                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="Service Name" value={api.service?.serviceName} /></Grid>
//                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="Cloud Provider" value={api.cloudProvider?.toUpperCase()} /></Grid>
//                                 {/* <Grid item xs={12} sm={6} md={3}><DetailItem label="Environment" value={capitalize(api.environment || "")} /></Grid> */}
//                             </Grid>

//                             <Divider sx={{ my: 2.5 }} />
//                             <Typography variant="subtitle1" fontFamily="Gilroy-SemiBold" sx={{ mb: 2 }}>API Backend Specification</Typography>
//                             <Grid container spacing={2.5}>
//                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="Path" value={api.path} /></Grid>
//                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="HTTP Method" value={api.httpMethod} /></Grid>
//                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="API Version" value={api.apiVersion} /></Grid>
//                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="Code Template" value={api.codeTemplate} /></Grid>
//                                 {/* {apiEndpoint && (
//                                                                 <Grid item xs={6} sm={3} >
//                                                                     <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>
//                                                                         API Backend Endpoint
//                                                                     </Typography>
//                                                                     <Tooltip
//                                                                         title={
//                                                                             <Link
//                                                                                 href={apiEndpoint}
//                                                                                 target="_blank"
//                                                                                 rel="noopener noreferrer"
//                                                                                 sx={{ color: '#ffffff', textDecoration: 'underline' }}
//                                                                             >
//                                                                             {apiEndpoint == 'Loading...'? apiEndpoint: "Endpoint"}
//                                                                             </Link>
//                                                                         }
//                                                                     >
//                                                                         <IconButton sx={{p: 0}}>
//                                                                             <Avatar
//                                                                                 src="/assets/info.svg"
//                                                                                 alt="info"
//                                                                                 sx={{ height: 16, width: 16, cursor: 'pointer' }}
//                                                                             />
//                                                                         </IconButton>
//                                                                     </Tooltip>
//                                                                 </Grid>
//                                                             )} */}
                                
//                                                             {apiRepo && (
//                                                                 <Grid item xs={6} sm={3}>
//                                                                     <Typography 
//                                                                         variant="caption" 
//                                                                         display="block" 
//                                                                         sx={{ fontFamily: 'Gilroy-Regular', fontSize: '12px', color: '#666666' }}
//                                                                     >
//                                                                         API Backend Repo
//                                                                     </Typography>
//                                                                     <Tooltip
//                                                                         title={
//                                                                             <>
//                                                                                 <Typography 
//                                                                                     fontFamily="Gilroy-Bold" 
//                                                                                     fontSize="14px"
//                                                                                 >
//                                                                                     API Backend Repo
//                                                                                 </Typography>
                                                                                
//                                                                                 {/* Conditionally render Link vs. plain text */}
//                                                                                 {apiRepo && apiRepo !== "Loading..." ? (
//                                                                                     <Link
//                                                                                         href={apiRepo}
//                                                                                         target="_blank"
//                                                                                         rel="noopener noreferrer"
//                                                                                         sx={{
//                                                                                             color: '#ffffff',
//                                                                                             textDecoration: 'underline',
//                                                                                             wordBreak: 'break-all' // Ensures long URLs wrap
//                                                                                         }}
//                                                                                     >
//                                                                                         Repository URL
//                                                                                     </Link>
//                                                                                 ) : (
//                                                                                     <Typography 
//                                                                                         fontFamily="Gilroy-Regular" 
//                                                                                         fontSize="12px" 
//                                                                                         sx={{ color: '#ffffff' }}
//                                                                                     >
//                                                                                         Loading...
//                                                                                     </Typography>
//                                                                                 )}
//                                                                             </>
//                                                                         }
//                                                                         slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
//                                                                     >
//                                                                         <IconButton sx={{ p: 0 }}>
//                                                                             <Avatar
//                                                                                 src="/assets/info.svg"
//                                                                                 alt="info"
//                                                                                 sx={{ height: 16, width: 16, cursor: 'pointer' }}
//                                                                             />
//                                                                         </IconButton>
//                                                                     </Tooltip>
//                                                                 </Grid>
//                                                             )}
//                             </Grid>
//                         </Box>
//                     </Box>
                    
//                     {api?.status === "pending" && (
//                         <Box display="flex" justifyContent="flex-end" gap={2} my={3}>
//                             <Button sx={{ color: "black",borderColor: "black",fontWeight: "bold",textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px" }} variant="outlined" size="large" onClick={() => setOpenModal(true)}>Reject Request</Button>
//                             <Button sx={{ backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px" }} variant="contained" size="large" onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
//                         </Box>
//                     )}

//                     <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
//                         <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 500 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px" }}>
//                             <Box display="flex" justifyContent="space-between" alignItems="center" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Request</Typography><IconButton onClick={() => setOpenApproveModal(false)}><Close fontSize="small" /></IconButton></Box>
//                             <Divider />
//                             <Box p={3}><Typography>Are you sure you want to approve this API Backend request?</Typography></Box>
//                             <Divider />
//                             <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
//                                 <Button variant="outlined" onClick={() => setOpenApproveModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
//                                 <Button onClick={handleApprove} sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } }} variant="contained">Approve</Button>
//                             </Box>
//                         </Box>
//                     </Modal>

//                     <Modal open={openModal} onClose={() => setOpenModal(false)}>
//                         <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 700 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px" }}>
//                             <Formik
//                                 initialValues={{ reasonForRejection: "" }}
//                                 validationSchema={ValidationSchema} // Make sure this is the schema from line 48, (which you should rename from ValidationSchema to rejectValidationSchema for clarity)
//                                 onSubmit={async ({ reasonForRejection }) => {
//                                     if (!id) return;
//                                     try {
//                                         // This is the correct mutation from the previous step
//                                         const rejectionData = await queryGraphql(`mutation RejectAPIBackend($input: RejectAPIBackendInput!) { rejectAPIBackend(input: $input) { id } }`,{ input: { id: id, reasonForRejection } });
//                                         if (rejectionData.rejectAPIBackend?.id) {
//                                             setOpenModal(false); // Use correct state
//                                             navigate("/apis/requests");
//                                         }
//                                     } catch (error) { console.error("Error during rejection:", error); }
//                                 }}
//                             >
//                                 {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
//                                     <Form id="reject-api-form"> {/* Correct form ID */}
//                                         <Box display="flex" justifyContent="space-between" alignItems="center" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject API Backend Request</Typography><IconButton onClick={() => setOpenModal(false)}><Close fontSize="small" /></IconButton></Box>
//                                         <Divider />
//                                         <Box sx={{ p: 3 }}>
//                                             <CompulsoryFieldsLabel fieldName="Comments" />
//                                             {/* Correct field name and value */}
//                                             <TextField variant="filled" fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
//                                             <Box sx={{ color: "red", mt: 1 }}><ErrorMessage name="reasonForRejection" /></Box>
//                                         </Box>
//                                         <Divider />
//                                         <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
//                                             <Button variant="outlined" onClick={() => setOpenModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
//                                             {/* Correct form ID and Button Text */}
//                                             <Button type="submit" form="reject-api-form" disabled={!isValid || !dirty} sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)' } }} variant="contained">Reject</Button>
//                                         </Box>
//                                     </Form>
//                                 )}
//                             </Formik>
//                         </Box>
//                     </Modal>
//                 </Grid>
//             </Grid>
//         </Container>
//     );
// };

// const APIApproveFormPage: React.FC = () => {
//     const theme = useTheme();
//     const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(() => typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('isFaradayDrawerOpen') || 'true') : true);
//     const handleDrawerStateChange = (isOpen: boolean) => {
//         setIsDrawerOpen(isOpen);
//         if (typeof window !== 'undefined') localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpen));
//     };
//     const currentMarginLeft = isDrawerOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer index={"requests-apis"} onStateChange={handleDrawerStateChange} />
//             <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
//                 <APIApproveFormPageContent />
//             </Box>
//         </Box>
//     );
// };

// export default APIApproveFormPage;